/**
 * Tag filter dropdown – reusable for Lists stats, LeadsColumn, LeadsSection.
 * Full-row colored options, hover feedback. For Media Devoted | Created By Ciro Autiero
 *
 * Optional `filteredContent`: when provided, renders inside the popover below tag options
 * so the user can filter and view results in one place. Popover stays open on tag select.
 */

import type { FC, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';

import { cn } from '@/lib/utils';

export const NO_TAGS_VALUE = '__none__';

/** Shared with MultiTagFilterDropdown so both render a tag row identically. */
export function getTagOptionStyle(
  tag: string,
  colors?: Record<string, string>,
  isSelected?: boolean,
) {
  const hex = colors?.[tag];
  if (!hex) return undefined;
  return {
    backgroundColor: `${hex}${isSelected ? '40' : '26'}`,
    color: hex,
  };
}

export interface TagFilterDropdownProps {
  value: string;
  options: string[];
  tagColors?: Record<string, string>;
  displayLabel: string;
  onSelect: (value: string) => void;
  placeholder?: string;
  /** Compact trigger height. Default: h-9 */
  compact?: boolean;
  /** Renders below tag options inside popover. When set, popover stays open on tag select. */
  filteredContent?: ReactNode;
}

export const TagFilterDropdown: FC<TagFilterDropdownProps> = ({
  value,
  options,
  tagColors,
  displayLabel,
  onSelect,
  placeholder = 'All tags',
  compact = false,
  filteredContent,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const hasContent = !!filteredContent;

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase().trim();
    return options.filter((tag) => tag.toLowerCase().includes(q));
  }, [options, search]);

  const showNoTags = !search.trim() || 'no tags'.includes(search.toLowerCase());

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch('');
  };

  const triggerStyle =
    value && value !== NO_TAGS_VALUE
      ? getTagOptionStyle(value, tagColors, true)
      : undefined;
  const triggerBorderStyle =
    triggerStyle && tagColors?.[value]
      ? { borderColor: `${tagColors[value]}60` }
      : undefined;

  const handleTagSelect = (v: string) => {
    onSelect(v);
    if (!hasContent) setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label="Filter by tag"
          className={cn(
            'flex min-w-[72px] shrink items-center justify-between gap-2 rounded border border-input px-2.5 text-left text-sm font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            !triggerStyle && 'bg-background text-foreground hover:bg-accent/50',
            compact ? 'h-8' : 'h-9',
          )}
          style={triggerStyle ? { ...triggerStyle, ...triggerBorderStyle } : undefined}
        >
          <span className="min-w-0 max-w-[100px] truncate">{displayLabel}</span>
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 opacity-80',
              open && 'rotate-180',
              triggerStyle ? '' : 'text-muted-foreground',
            )}
            style={triggerStyle ? { color: triggerStyle.color } : undefined}
          />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className={cn(
            'z-[250] min-w-[200px] max-w-[min(300px,70vw)] rounded border border-border bg-popover text-popover-foreground shadow-lg',
            'p-0',
            hasContent && 'min-w-[280px] max-w-[min(340px,85vw)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div className="border-b border-border p-2">
            <div className="flex items-center gap-2 rounded-md border border-input bg-background px-2 py-1.5">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                type="search"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                autoFocus
                autoComplete="off"
              />
            </div>
          </div>
          <div
            role="listbox"
            className={cn(
              hasContent ? 'max-h-[180px] overflow-y-auto border-b border-border py-1' : 'max-h-[260px] overflow-y-auto py-1',
            )}
          >
            <button
                type="button"
                role="option"
                aria-selected={value === ''}
                className={cn(
                  'group flex w-full items-center px-3 py-2 text-left text-sm font-medium transition-colors',
                  'hover:bg-accent hover:text-accent-foreground',
                  value === '' && 'bg-accent text-accent-foreground',
                )}
                onClick={() => handleTagSelect('')}
              >
                {placeholder}
              </button>
              {filteredOptions.map((tag) => {
                const isSelected = value === tag;
                const tagStyle = getTagOptionStyle(tag, tagColors, isSelected);
                return (
                  <button
                    key={tag}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={cn(
                      'group relative flex w-full items-center overflow-hidden px-3 py-2 text-left text-sm font-medium transition-colors',
                      !tagStyle && 'hover:bg-accent/60',
                      !tagStyle && isSelected && 'bg-accent/70',
                      isSelected && tagStyle && 'ring-1 ring-inset ring-black/15 dark:ring-white/25',
                    )}
                    style={tagStyle}
                    onClick={() => handleTagSelect(tag)}
                  >
                    {tagStyle && (
                      <span
                        className="pointer-events-none absolute inset-0 bg-white/20 opacity-0 transition-opacity duration-150 group-hover:opacity-100 dark:bg-black/20"
                        aria-hidden
                      />
                    )}
                    <span className="relative">{tag}</span>
                  </button>
                );
              })}
              {showNoTags && (
                <button
                  type="button"
                  role="option"
                  aria-selected={value === NO_TAGS_VALUE}
                  className={cn(
                    'flex w-full items-center px-3 py-2 text-left text-sm font-medium text-muted-foreground transition-colors',
                    'hover:bg-accent hover:text-accent-foreground',
                    value === NO_TAGS_VALUE && 'bg-accent text-accent-foreground',
                  )}
                  onClick={() => handleTagSelect(NO_TAGS_VALUE)}
                >
                  No tags
                </button>
              )}
          </div>
          {hasContent && (
            <div className="max-h-[280px] overflow-y-auto p-1.5">
              {filteredContent}
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
